import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch } from "../../store";
import { resetPassword, forgotPassword } from "../../store/actions/auth.action";
import { decodeToken } from "../../utils/local-storage";
import ToastMessage from "../../utils/toastMessage";
import { App, Button } from "antd";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import logoImage from "../../assets/images/logo.svg";
import theme from "../../themes";
import {
  authCardCss,
  authFieldRowCss,
  authLabelCss,
  authPageCss,
} from "../../styles/responsive";

interface ResetPasswordForm {
  password: string;
  confirmPassword: string;
}

interface DecodedToken {
  exp: number;
  iat: number;
  email?: string;
  [key: string]: any;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isDecoding, setIsDecoding] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [decodedTokenData, setDecodedTokenData] = useState<DecodedToken | null>(
    null
  );
  const [requestingNewLink, setRequestingNewLink] = useState(false);
  const { token } = useParams();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  // Handle token decoding and countdown timer
  useEffect(() => {
    if (token) {
      setIsDecoding(true);
      setTokenError(null);
      try {
        const decoded = decodeToken(token) as DecodedToken;
        setDecodedTokenData(decoded);
        const expirationTime = decoded.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeRemaining = Math.max(
          0,
          Math.floor((expirationTime - currentTime) / 1000)
        );

        if (timeRemaining <= 0) {
          setTokenError("expired");
          setCountdown(0);
        } else {
          setCountdown(timeRemaining);

          // Start countdown timer
          const timer = setInterval(() => {
            setCountdown((prevCountdown) => {
              if (prevCountdown <= 1) {
                setTokenError("expired");
                return 0;
              }
              return prevCountdown - 1;
            });
          }, 1000);

          // Cleanup timer on unmount or when token changes
          return () => clearInterval(timer);
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        setTokenError("invalid");
        setCountdown(0);
      } finally {
        setIsDecoding(false);
      }
    } else {
      setIsDecoding(false);
    }
  }, [token]);

  // Handle requesting new reset link
  const handleRequestNewLink = async () => {
    if (!decodedTokenData?.email) {
      toast.error(
        "Email not found in token. Please go to forgot password page."
      );
      navigate("/forgot-password");
      return;
    }

    setRequestingNewLink(true);
    try {
      await dispatch(
        forgotPassword({ email: decodedTokenData.email })
      ).unwrap();
      toast.success(
        "New reset link sent to your email!",
        5,
        <CheckCircleIcon />
      );
    } catch (error: any) {
      console.error("Error requesting new link:", error);
      toast.error(error.message || "Failed to send new reset link", 5);
    } finally {
      setRequestingNewLink(false);
    }
  };

  // Handle fallback to forgot password page
  const handleGoToForgotPassword = () => {
    navigate("/forgot-password");
  };

  // Format countdown to MM:SS
  const formatCountdown = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const isExpiring = countdown <= 30; // Show warning when 30 seconds or less

  // Yup validation schema
  const schema = yup
    .object({
      password: yup
        .string()
        .required("Password is required")
        .min(6, "Password must be at least 6 characters"),
      confirmPassword: yup
        .string()
        .required("Please confirm your password")
        .oneOf([yup.ref("password")], "Passwords must match"),
    })
    .required();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    setLoading(true);

    try {
      await dispatch(
        resetPassword({ password: data.password, token: token as string })
      ).unwrap();
      reset();
      toast.success("Password reset successfully", 5, <CheckCircleIcon />);
      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (err) {
      setTokenError("invalid");
    } finally {
      setLoading(false);
    }
  };

  if (!token || tokenError) {
    const getErrorContent = () => {
      switch (tokenError) {
        case "expired":
          return {
            title: "Reset Link Expired",
            subtitle: "This password reset link has expired",
            message:
              "This password reset link has expired. Please request a new one to continue.",
          };
        case "invalid":
          return {
            title: "Invalid Reset Link",
            subtitle: "The reset link format is invalid",
            message:
              "The reset link format is invalid or corrupted. Please request a new password reset.",
          };
        default:
          return {
            title: "Invalid Reset Link",
            subtitle: "The reset link is invalid or has expired",
            message: "Invalid reset link. Please request a new password reset.",
          };
      }
    };

    const errorContent = getErrorContent();

    return (
      <LoginContainer>
        <LogoContainer>
          <LogoWrapper>
            <LogoImage src={logoImage} alt="Lytte.ai Logo" />
            <Title>Lytte.ai</Title>
          </LogoWrapper>
        </LogoContainer>

        <Card>
          <PageTitle>{errorContent.title}</PageTitle>
          <Subtitle>{errorContent.subtitle}</Subtitle>
          <FormError role="alert" style={{ marginTop: 16 }}>
            {errorContent.message}
          </FormError>

          <Divider />

          <RightAlignRow>
            {decodedTokenData?.email ? (
              <ActionButton
                onClick={handleRequestNewLink}
                disabled={requestingNewLink}
              >
                {requestingNewLink
                  ? "Sending New Reset Link..."
                  : "Request New Reset Link"}
              </ActionButton>
            ) : (
              <ActionButton onClick={handleGoToForgotPassword}>
                Go to Forgot Password
              </ActionButton>
            )}
          </RightAlignRow>
        </Card>
      </LoginContainer>
    );
  }

  if (isDecoding) {
    return (
      <LoginContainer>
        <LogoContainer>
          <LogoWrapper>
            <LogoImage src={logoImage} alt="Lytte.ai Logo" />
            <Title>Lytte.ai</Title>
          </LogoWrapper>
        </LogoContainer>

        <Card>
          <PageTitle>Verifying Reset Link</PageTitle>
          <Subtitle>Please wait while we verify your reset link...</Subtitle>
        </Card>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer>
      <LogoContainer>
        <LogoWrapper>
          <LogoImage src={logoImage} alt="Lytte.ai Logo" />
          <Title>Lytte.ai</Title>
        </LogoWrapper>
      </LogoContainer>

      <Card>
        <WelcomeMessage>Please enter your new password below.</WelcomeMessage>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {decodedTokenData?.email && (
            <FormGroup style={{ marginBottom: "16px" }}>
              <FieldRow>
                <Label>Email</Label>
                <StaticText>{decodedTokenData.email}</StaticText>
              </FieldRow>
            </FormGroup>
          )}

          <FormGroup style={{ marginBottom: "16px" }}>
            <FieldRow>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter new password"
                disabled={loading}
                className={errors.password ? "error" : ""}
                autoFocus
                style={{ flex: 1 }}
                {...register("password")}
              />
            </FieldRow>
          </FormGroup>

          <FormGroup>
            <FieldRow>
              <Label htmlFor="confirmPassword">Re-enter new password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Re-enter new password"
                disabled={loading}
                className={errors.confirmPassword ? "error" : ""}
                style={{ flex: 1 }}
                {...register("confirmPassword")}
              />
            </FieldRow>
          </FormGroup>

          {errors.password && (
            <FieldError>{errors.password.message}</FieldError>
          )}
          {errors.confirmPassword && (
            <FieldError>{errors.confirmPassword.message}</FieldError>
          )}

          <Divider />

          <ButtonRow>
            <Countdown>
              Reset link expires in:{" "}
              <CountdownTimer isExpiring={isExpiring}>
                {formatCountdown(countdown)}
              </CountdownTimer>
            </Countdown>
            <ActionButton
              htmlType="submit"
              disabled={loading || !isValid}
              className={isValid ? "active" : ""}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </ActionButton>
          </ButtonRow>
        </Form>
      </Card>
    </LoginContainer>
  );
};

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  ${authPageCss}
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 48px;

  @media (max-width: 640px) {
    margin-bottom: 24px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const Title = styled.h1`
  color: #333;
  text-align: center;
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  ${authCardCss}
`;

const PageTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0 0 12px 0;
  color: #6b7280;
`;

const WelcomeMessage = styled.p`
  text-align: left;
  margin: 0 0 32px 0;
  font-size: 18px;
  font-weight: 500;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldRow = styled.div`
  ${authFieldRowCss}
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 16px;
  text-align: left;
  ${authLabelCss}
  width: 200px;

  @media (max-width: 640px) {
    width: auto;
  }
`;

const StaticText = styled.div`
  font-size: 16px;
`;

const Input = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  background: white;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00b4d8;
  }

  &:hover {
    border-color: #bdbdbd;
  }

  &::placeholder {
    color: #9e9e9e;
  }

  &.error {
    border-color: #e74c3c;
  }
`;

const FieldError = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 24px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const RightAlignRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ActionButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #047857 !important;
    color: #fff !important;
  }

  &:disabled {
    background: ${theme.colors.grey2};
    cursor: not-allowed;
  }
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background: #e5e7eb;
  margin: 32px -16px 24px -16px;
`;

const Countdown = styled.p`
  color: #0369a1;
  font-size: 14px;
`;

const CountdownTimer = styled.span<{ isExpiring: boolean }>`
  font-weight: 700;
  color: ${({ isExpiring }) => (isExpiring ? "#e74c3c" : "#0369a1")};
`;

const FormError = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 24px;
`;

export default ResetPassword;
